import {
  Injectable,
  signal,
  computed,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Service for managing frozen (pinned) columns in tables
 * Provides reactive state management and localStorage persistence
 *
 * Features:
 * - Maximum 5 frozen columns
 * - Maintains original table order (not selection order)
 * - Persists configuration in localStorage
 * - Reactive signals for immediate UI updates
 * - SSR-safe with platform detection
 * - Configurable storage key per module
 *
 * @story HU04-898440 - Inmovilizar Columnas
 */
@Injectable()
export class FrozenColumnsService {
  private readonly MAX_FROZEN_COLUMNS = 5;
  private readonly DEFAULT_STORAGE_KEY = 'sig-frozen-columns';
  private readonly storageKey: string;

  /**
   * Signal for currently frozen column keys
   */
  private readonly frozenColumnsSignal = signal<string[]>([]);

  /**
   * Computed signal for frozen columns count
   */
  readonly frozenColumnsCount = computed(
    () => this.frozenColumnsSignal().length
  );

  /**
   * Computed signal to check if limit is reached
   */
  readonly isLimitReached = computed(
    () => this.frozenColumnsSignal().length >= this.MAX_FROZEN_COLUMNS
  );

  /**
   * Get current frozen columns as readonly signal
   */
  readonly frozenColumns = this.frozenColumnsSignal.asReadonly();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject('FROZEN_COLUMNS_STORAGE_KEY') storageKey?: string
  ) {
    this.storageKey = storageKey ?? this.DEFAULT_STORAGE_KEY;
    // Load from localStorage on initialization (browser only)
    if (isPlatformBrowser(this.platformId)) {
      this.loadFromStorage();
    }
  }

  /**
   * Toggle a column frozen state
   * @param columnKey Column key to toggle
   * @returns true if successful, false if limit reached
   */
  toggleColumn(columnKey: string): boolean {
    const current = this.frozenColumnsSignal();
    const isCurrentlyFrozen = current.includes(columnKey);

    if (isCurrentlyFrozen) {
      this.updateFrozenColumns(current.filter((key) => key !== columnKey));
      return true;
    }

    if (current.length >= this.MAX_FROZEN_COLUMNS) {
      return false;
    }

    this.updateFrozenColumns([...current, columnKey]);
    return true;
  }

  /**
   * Check if a column is frozen
   * @param columnKey Column key to check
   */
  isColumnFrozen(columnKey: string): boolean {
    return this.frozenColumnsSignal().includes(columnKey);
  }

  /**
   * Clear all frozen columns
   */
  clearAll(): void {
    this.updateFrozenColumns([]);
  }

  /**
   * Update frozen columns and persist to storage
   */
  private updateFrozenColumns(columns: string[]): void {
    this.frozenColumnsSignal.set(columns);
    this.saveToStorage(columns);
  }

  /**
   * Get frozen columns sorted by original table order
   * @param allColumns All column keys in original table order
   */
  getFrozenColumnsInOrder(allColumns: string[]): string[] {
    return this.filterColumnsByFrozenState(allColumns, true);
  }

  /**
   * Get non-frozen columns in original order
   * @param allColumns All column keys in original table order
   */
  getNonFrozenColumnsInOrder(allColumns: string[]): string[] {
    return this.filterColumnsByFrozenState(allColumns, false);
  }

  /**
   * Filter columns by frozen state
   */
  private filterColumnsByFrozenState(
    allColumns: string[],
    isFrozen: boolean
  ): string[] {
    const frozen = this.frozenColumnsSignal();
    return allColumns.filter((col) => frozen.includes(col) === isFrozen);
  }

  /**
   * Save frozen columns to localStorage (browser only)
   * Handles storage errors gracefully without breaking UI
   */
  private saveToStorage(columns: string[]): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(columns));
    } catch {
      // Storage quota exceeded, disabled, or permission denied
      // Continue without persisting - UI remains functional
      // GlobalErrorHandler will log this in development mode
    }
  }

  /**
   * Load frozen columns from localStorage (browser only)
   * Note: Only called from constructor after platform check
   * Handles corrupted data gracefully by cleaning invalid entries
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);

      if (stored) {
        const columns = JSON.parse(stored) as unknown;
        if (Array.isArray(columns)) {
          this.frozenColumnsSignal.set(columns as string[]);
        } else {
          // Corrupted data: clean to prevent future failures
          localStorage.removeItem(this.storageKey);
        }
      }
    } catch {
      // JSON parse error or storage access denied: clean and continue with empty state
      // GlobalErrorHandler will log this in development mode
      try {
        localStorage.removeItem(this.storageKey);
      } catch {
        // If removal also fails (storage locked), continue silently with empty state
      }
    }
  }

  /**
   * Get maximum allowed frozen columns
   */
  getMaxFrozenColumns(): number {
    return this.MAX_FROZEN_COLUMNS;
  }
}
